import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Banner,
    BannerImage,
    BannerImageContainer,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    BoundedContent,
    FlexBox,
    Icon,
    IconButton,
    Li,
    Ol,
} from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import styles, { stylesheet } from './FeatureBannerHowItWorks.module.scss';

const FeatureBannerHowItWorks = () => {
    useStyles(stylesheet);

    const { t } = useTranslation();
    const playTooltip = t(
        'Logomaker GenAi Landing Page > Feature Banner How It Works > Media Control > Tooltip > Play.value',
    );
    const pauseTooltip = t(
        'Logomaker GenAi Landing Page > Feature Banner How It Works > Media Control > Tooltip > Pause.value',
    );
    const mutedTooltip = t(
        'Logomaker GenAi Landing Page > Feature Banner How It Works > Media Control > Tooltip > Muted.value',
    ) as string;
    const unmutedTooltip = t(
        'Logomaker GenAi Landing Page > Feature Banner How It Works > Media Control > Tooltip > Unmuted.value',
    ) as string;
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const playPauseTooltipText = isPlaying ? pauseTooltip : playTooltip;
    const muteTooltipText = isMuted ? mutedTooltip : unmutedTooltip;

    const handlePlayPause = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
            video.play();
        } else {
            video.pause();
        }
    }, []);

    const handleMuteToggle = () => {
        setIsMuted(!isMuted);
    };

    const distributionUrl = t(
        'Logomaker GenAi Landing Page > Feature Banner How It Works > Video.cloudinaryVideo.distributionUrl',
    );
    const publicIdTemplate = t(
        'Logomaker GenAi Landing Page > Feature Banner How It Works > Video.cloudinaryVideo.publicIdTemplate',
    );
    const videoBaseUrl =
        distributionUrl && publicIdTemplate
            ? `${distributionUrl}video/upload/f_auto,q_auto/${publicIdTemplate.replace(/^\//, '')}`
            : '';

    return (
        <BoundedContent as="section" backgroundColor="accent" id="how-it-works" py={8}>
            <Banner backgroundColor="accent">
                <BannerImageContainer className={styles.bannerImageContainer}>
                    <BannerImage className={styles.swanBannerImage}>
                        <video
                            ref={videoRef}
                            poster={t(
                                'Logomaker GenAi Landing Page > Feature Banner How It Works > Video > Poster Image.src',
                            )}
                            preload="metadata"
                            playsInline
                            loop
                            autoPlay
                            muted={isMuted}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                            onError={(e) => console.error('Video error:', e)}
                        >
                            {videoBaseUrl && (
                                <>
                                    <source src={`${videoBaseUrl}.webm`} type="video/webm" />
                                    <source src={`${videoBaseUrl}.mp4`} type="video/mp4" />
                                </>
                            )}
                        </video>
                    </BannerImage>
                    <FlexBox gap={3} className={styles.controllersWrapper}>
                        {/* eslint-disable-next-line @vp/swan/enforce-iconbutton-structure */}
                        <IconButton
                            className={styles.mediaController}
                            skin="tertiary"
                            tooltip={muteTooltipText}
                            onClick={handleMuteToggle}
                            aria-label={muteTooltipText}
                        >
                            <img
                                style={{ padding: '4px' }}
                                src={`https://cms.cloudinary.vpsvc.com/image/upload/vista-logos-pages/en-US/logomaker-gen-ai/video-controls/${isMuted ? 'muted' : 'unmuted'}.svg`}
                                alt={`${isMuted ? 'muted' : 'unmuted'} video`}
                            />
                        </IconButton>
                        <IconButton
                            className={styles.mediaController}
                            skin="tertiary"
                            tooltip={playPauseTooltipText}
                            onClick={handlePlayPause}
                            aria-label={playPauseTooltipText}
                        >
                            <Icon
                                iconType={isPlaying ? 'pause' : 'play'}
                                size="24p"
                                skin="white"
                                className={styles.mediaIcon}
                            />
                        </IconButton>
                    </FlexBox>
                </BannerImageContainer>
                <BannerTextContainer>
                    <BannerText textAlign={'left'}>
                        <BannerTitle as="h2" fontSkin="title-section">
                            {t(
                                'Logomaker GenAi Landing Page > Feature Banner How It Works > Title.value',
                            )}
                        </BannerTitle>
                        <Ol skin={'standard'} start={1} paddingTop={5}>
                            <Li>
                                {t(
                                    'Logomaker GenAi Landing Page > Feature Banner How It Works > Description > List 1.value',
                                )}
                            </Li>
                            <Li>
                                {t(
                                    'Logomaker GenAi Landing Page > Feature Banner How It Works > Description > List 2.value',
                                )}
                            </Li>
                            <Li>
                                {t(
                                    'Logomaker GenAi Landing Page > Feature Banner How It Works > Description > List 3.value',
                                )}
                            </Li>
                            <Li>
                                {t(
                                    'Logomaker GenAi Landing Page > Feature Banner How It Works > Description > List 4.value',
                                )}
                            </Li>
                        </Ol>
                    </BannerText>
                </BannerTextContainer>
            </Banner>
        </BoundedContent>
    );
};
export default FeatureBannerHowItWorks;
